import styled from "styled-components";
import React, { SVGProps, useEffect, useMemo, useState } from "react";
import { graphql, prefetchPathname, useStaticQuery } from "gatsby";
import { brandColors, theme } from "../../theme/theme";
import { BurgerIcon } from "../BurgerIcon";
import { mediaQuery } from "../../theme/constants";
import { ContentContainer } from "./ContentContainer";
import { Link } from "../Link";
import { LanguageSelectButton } from "./LanguageSelectButton";
import { useLocale } from "../../hooks/useLocale";
import { findByLocale } from "./findByLocale";
import { title } from "process";
import { Chevron } from "../Chevron";

const Query = graphql`
  query NavBarQuery($locale: String) {
    pricing: datoCmsPricing(locale: $locale) {
      title
      _allTitleLocales {
        locale
        value
      }
      slug
    }
    story: datoCmsStory(locale: $locale) {
      title
      _allTitleLocales {
        locale
        value
      }
      slug
    }
    references: datoCmsReferencesPage(locale: $locale) {
      title
      _allTitleLocales {
        locale
        value
      }
      slug
    }
    blog: datoCmsBlog(locale: $locale) {
      title
      _allTitleLocales {
        locale
        value
      }
      slug
    }
    contact: datoCmsContact(locale: $locale) {
      title
      _allTitleLocales {
        locale
        value
      }
      slug
      contactCta
      _allContactCtaLocales {
        locale
        value
      }
    }
    global: datoCmsGlobal(locale: $locale) {
      guidinLogo {
        alt
        url
      }
      testAccountCta {
        ctaButton
        ctaUrl
        ctaButtonShortTitle
      }
    }
    consultant: datoCmsConsultant(locale: $locale) {
      title
      slug
      _allTitleLocales {
        locale
        value
      }
    }
    change: datoCmsChange(locale: $locale) {
      slug
      _allTitleLocales {
        locale
        value
      }
    }
    changeArticles: allDatoCmsChangeArticle(locale: $locale) {
      edges {
        node {
          slug
          _allTitleLocales {
            locale
            value
          }
        }
      }
    }
  }
`;

export const Navbar = ({
  flatMenu,
  slug
}: {
  flatMenu: boolean;
  slug?: string;
}) => {
  const locale = useLocale();
  const [menuOpen, setMenuOpen] = useState(false);

  const data = useStaticQuery(Query);

  const isContactPage = slug === data.contact.slug;

  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
  }, [menuOpen]);

  const contactButton = data.contact as {
    title: string;
    slug: string;
    contactCta: string;
    _allContactCtaLocales: any;
  };

  const changeMenu = {
    title: data.change.title,
    _allTitleLocales: data.change._allTitleLocales,
    subMenu: data.changeArticles.edges
      .map((e: any) => e.node)
      .map((node: any) => ({
        ...node,
        slug: `${data.change.slug}/${node.slug}`
      })) as { _allTitleLocales: any[]; slug: string }[]
  };

  const mainMenu = [
    [changeMenu, false],
    [data.references, true],
    [data.pricing, true],
    [data.blog, false],
    [data.story, false],
    [data.consultant, true]
  ]
    .filter((menu) => {
      const englishVisible = menu[1] === true;
      return locale === "fi" || englishVisible;
    })
    .map((menu) => menu[0]);

  const companyLogo = data.global.guidinLogo;

  return (
    <>
      <Header className={flatMenu ? "flat" : ""}>
        <Container>
          <MainRow>
            <LogoContainer flat={flatMenu}>
              <NavLink to={`/`} activeClassName="active" aria-label="Home">
                <img
                  src={companyLogo.url}
                  alt={companyLogo.alt}
                  aria-label="To home page"
                />
              </NavLink>
            </LogoContainer>
            <NavItems flat={flatMenu}>
              {mainMenu.map((item) =>
                item.subMenu ? (
                  <SubMenu item={item} locale={locale} flat={flatMenu} />
                ) : (
                  <NavLink
                    to={`/${item.slug}`}
                    activeClassName="active"
                    key={item.slug}
                  >
                    {findByLocale(item._allTitleLocales, locale)}
                  </NavLink>
                )
              )}
            </NavItems>
            <div style={{ flexGrow: 1 }} />
            <LanguageSelectButton
              className={"localeLinkDesktop"}
              disabledPaths={[...changeMenu.subMenu.map((s) => s.slug)]}
            />
            {locale === "en" ? (
              !isContactPage && (
                <CtaButtonContainer>
                  <CtaButton
                    to={`/${contactButton.slug}`}
                    style={{ backgroundColor: brandColors.purple }}
                  >
                    {findByLocale(contactButton._allContactCtaLocales, locale)}
                  </CtaButton>
                </CtaButtonContainer>
              )
            ) : (
              <CtaButtonContainer>
                <CtaButton
                  to={data.global.testAccountCta.ctaUrl}
                  target="_target"
                >
                  {data.global.testAccountCta.ctaButtonShortTitle}
                </CtaButton>
              </CtaButtonContainer>
            )}
            <BurgerIcon
              menuOpen={menuOpen}
              onClick={() => setMenuOpen(!menuOpen)}
            />
          </MainRow>
        </Container>
      </Header>
      <MobileNavMenu locale={locale} menuOpen={menuOpen} mainMenu={mainMenu} />
    </>
  );
};

const SubMenu = ({
  item,
  locale,
  flat
}: {
  locale: string;
  flat: boolean;
  item: {
    _allTitleLocales: { value: string; locale: string }[];
    subMenu: {
      _allTitleLocales: { value: string; locale: string }[];
      slug: string;
    }[];
  };
}) => {
  const [open, setOpen] = useState(false);
  const handleBlur = (e: any) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setOpen(false);
    }
  };

  return (
    <SubMenuContainer open={open} onBlur={handleBlur}>
      <SubMenuParent
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen(!open)}
      >
        <span>{findByLocale(item._allTitleLocales, locale)}</span>
        <AbsoluteChevron flat={flat} width={10} height={10} />
      </SubMenuParent>

      <SubMenuChildrenContainer aria-expanded={open}>
        {item.subMenu.map((subItem) => (
          <NavLink
            to={`/${subItem.slug}`}
            key={subItem.slug}
            activeClassName="active"
          >
            {findByLocale(subItem._allTitleLocales, locale)}
          </NavLink>
        ))}
      </SubMenuChildrenContainer>
    </SubMenuContainer>
  );
};

const MobileNavMenu = ({
  menuOpen,
  mainMenu,
  locale
}: {
  menuOpen: boolean;
  mainMenu: {
    _allTitleLocales: any;
    slug?: string;
    subMenu?: { _allTitleLocales: any; slug: string }[];
  }[];
  locale: string;
}) => {
  return (
    <MobileMenu className={menuOpen ? "menuOpen" : ""}>
      <ContentContainer>
        <nav className="Mobile">
          <Link to={`/`} activeClassName="active" aria-label="Home">
            {locale === "fi" ? "Kotisivu" : "Home"}
          </Link>
          {mainMenu.map((item) => {
            return item.subMenu ? (
              <SubMenu item={item as any} flat={false} locale={locale} />
            ) : (
              <Link
                to={`/${item.slug}`}
                activeClassName="active"
                key={item.slug}
              >
                {findByLocale(item._allTitleLocales, locale)}
              </Link>
            );
          })}
          <LanguageSelectButton />
        </nav>
      </ContentContainer>
    </MobileMenu>
  );
};

const Container = styled(ContentContainer)`
  justify-content: center;
`;

const CtaButtonContainer = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
`;
const CtaButton = styled(Link)`
  padding-left: 0px;
  padding-right: 0px;
  padding: 0px 8px;
  letter-spacing: 0.5px;
  font-size: 11px;
  min-width: 135px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  border-radius: 28px;
  font-family: "Source sans 3", sans-serif;
  font-weight: 600;
  text-transform: uppercase;
  background: ${brandColors.orange};
  color: #fff;
  &:hover {
    transition: all 0.2s ease 0s;
    opacity: 0.92;
  }
  ${mediaQuery.medium} {
    margin-right: 20px;
  }
`;

const AbsoluteChevron = styled(Chevron)<{ flat: boolean }>`
  position: absolute;
  left: 50%;

  transition: all 0.3s;
  top: calc(50% + 12px + ${(p) => (p.flat ? 8 : 16)}px);
  transform: translate(-50%, -50%);

  @media screen and (max-width: 1051px) {
    position: initial;
    transform: none;
  }
`;

const SubMenuParent = styled.button`
  height: 100%;
  gap: 8px;
  font-weight: 600;
  padding: 0px 20px;
  margin: 0;
  color: ${theme.primary};
  position: relative;

  @media screen and (max-width: 1051px) {
    display: flex;
    text-align: center;
    align-items: center;
    justify-content: center;

    font-weight: 400;
  }
`;

const SubMenuContainer = styled.nav<{ open: boolean }>`
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  :focus {
    > nav:last-of-type {
      display: flex;
    }
  }
  > nav:last-of-type {
    display: ${(props) => (props.open ? "flex" : "none")};
  }

  @media screen and (max-width: 1051px) {
    width: 100%;
    position: initial;
  }
  @media screen and (min-width: 1052px) {
    :hover {
      > nav:last-of-type {
        display: flex;
      }
    }
  }
  :has(.active) {
    span,
    svg path {
      color: ${theme.accent};
      fill: ${theme.accent};
    }
  }
`;

const NavLink = styled(Link)`
  height: 100%;
  font-weight: 600;
  padding: 5px 20px;
  margin: 0;
  display: inline-flex;
  align-items: center;
  color: ${theme.primary};
  white-space: nowrap;
`;

const SubMenuChildrenContainer = styled.nav`
  position: absolute;
  min-width: 100%;
  flex-direction: column;
  bottom: 0%;
  transform: translateY(calc(100% - 4px));
  left: 0px;
  background-color: white;
  box-shadow: -4px 6px 5px rgba(0, 0, 0, 0.15);
  a {
    height: 60px;
    &:after {
      display: none !important;
    }
  }
  @media screen and (max-width: 1051px) {
    position: initial;
    display: flex;
    transform: none;
    box-shadow: none;
    width: auto;
    min-width: auto;

    > a {
      font-size: 18px !important;
      font-weight: 400;
      height: auto;
      justify-content: center;
      align-items: center;
    }
  }
`;

const NavItems = styled.nav<{ flat: boolean }>`
  display: flex;
  align-items: center;
  height: 100%;
  a {
    text-align: center;
    position: relative;
    transition: all 0.3s;
    &:hover {
      color: ${theme.accent};
    }
  }
  a.active {
    color: ${theme.accent};
    &:after {
      transition: all 0.3s;
      content: "";
      display: block;
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: ${theme.accent};
      position: absolute;
      left: calc(50% - 3px);
      bottom: ${(props) => (props.flat ? "8px" : "16px")};
    }
  }

  @media (max-width: 1051px) {
    display: none;
  }
`;

const LogoContainer = styled.div<{ flat?: boolean }>`
  height: 100%;
  width: 140px;
  transition: all 0.3s;
  @media (max-width: 380px) {
    width: 90px;
  }
  a {
    display: flex;
    align-items: center;
    flex-direction: row;
    padding-left: 0;
    padding-bottom: 0;
  }
  img {
    max-width: ${(props) => (props.flat ? "100px" : "130px")};
    transition: all 0.3s;
    ${mediaQuery.medium} {
      max-width: 100px;
      margin-top: -8px;
    }
  }
`;

const MainRow = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  transition: all 0.3s;
  display: flex;
  align-items: center;
`;

const Header = styled.header`
  height: 100px;
  width: 100%;
  z-index: 10;
  background: #fff;
  display: flex;
  align-items: center;
  box-shadow: 0 8px 10px 0 rgba(0, 0, 0, 0.05);
  position: fixed;
  transition: all 0.3s;
  ${mediaQuery.medium} {
    height: 64px;
  }

  &.flat {
    box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.07);
    height: 64px;
    ${MainRow} {
      @media screen and (min-width: 1051px) {
        margin-top: -8px;
      }
    }
    ${LogoContainer} {
      svg {
        width: 90px;
      }
    }
  }
`;

const MobileMenu = styled.div`
  @media screen and (min-width: 1051px) {
    display: none;
  }
  display: flex;
  width: 100%;
  height: 100vh;
  transform: translate(0, -100vh);
  opacity: 0;
  transition:
    opacity 0.3s,
    transform 0s;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 9;
  background: #fff;
  padding-top: 40px;
  > div {
    overflow-y: scroll;
    padding-bottom: 40px;
  }
  &.menuOpen {
    transform: translate(0, 0);
    opacity: 1;
  }
  nav a,
  nav button {
    width: 100%;
    color: ${theme.primary};
    font-family: "Source sans 3";
  }
  nav a.active {
    color: ${theme.accent};
  }
  .Divider {
    width: 100%;
    height: 25px;
  }
  nav.Mobile {
    width: 100%;
    max-width: 320px;
    display: flex;
    flex-direction: column;
    align-items: center;

    a,
    button {
      font-family: "Source sans 3", sans-serif;
      font-size: 22px;
      text-align: center;
      padding: 11px 20px;
    }
  }
  nav.Buttons {
    display: none;
    flex-direction: column;
    align-items: center;
    a,
    button {
      margin: 10px auto;
      font-size: 12px;
    }
  }
  nav.Locales {
    display: flex;
    width: 80%;
    font-size: 16px;
    margin-top: 30px;
    padding-left: 20px;
    padding-right: 20px;
    border-top: 1px solid ${theme.primary};
    justify-content: center;
    a {
      width: fit-content;
      padding: 10px;
      font-size: 15px;
    }
  }
  > div {
    padding-top: 70px;
    display: flex;
    height: 100%;
    width: 100%;
    flex-direction: column;
    align-items: center;
  }
`;
